import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';

const AdministracionCanchas = () => {
  const [canchas, setCanchas] = useState([]);
  const [tiposDeporte, setTiposDeporte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCancha, setEditingCancha] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar tipos de deporte
  const loadTiposDeporte = async () => {
    try {
      const response = await apiClient.get('/club/tipos-deporte/activos');
      if (response.ok) {
        const data = await response.json();
        setTiposDeporte(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de deporte:', error);
    }
  };

  // Cargar canchas (usando el endpoint del club)
  const loadCanchas = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/club/canchas');
      if (response.ok) {
        const data = await response.json();
        setCanchas(data);
      } else {
        message.error('Error al cargar las canchas');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las canchas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTiposDeporte();
    loadCanchas();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nueva cancha
  const handleCreate = () => {
    setEditingCancha(null);
    form.resetFields();
    form.setFieldsValue({ activa: true, techada: false });
    setModalVisible(true);
  };

  // Abrir modal para editar cancha
  const handleEdit = (record) => {
    setEditingCancha(record);
    form.setFieldsValue({
      nombre: record.ccl_nombre,
      tipoDeporte: record.ccl_tipo_deporte,
      techada: record.ccl_techada,
      activa: record.ccl_activa,
    });
    setModalVisible(true);
  };

  // Guardar cancha (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCancha) {
        // Actualizar
        const response = await apiClient.put(`/club/canchas/${editingCancha.ccl_id}`, values);
        if (response.ok) {
          message.success('Cancha actualizada correctamente');
          setModalVisible(false);
          loadCanchas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la cancha');
        }
      } else {
        // Crear
        const response = await apiClient.post('/club/canchas', values);
        if (response.ok) {
          message.success('Cancha creada correctamente');
          setModalVisible(false);
          loadCanchas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la cancha');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al guardar la cancha');
    }
  };

  // Eliminar cancha
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/club/canchas/${id}`);
      if (response.ok) {
        message.success('Cancha eliminada correctamente');
        loadCanchas();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la cancha');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la cancha');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'ccl_nombre',
      key: 'ccl_nombre',
    },
    {
      title: 'Tipo de Deporte',
      dataIndex: 'tipo_deporte_nombre',
      key: 'tipo_deporte_nombre',
      render: (text) => text ? <Tag color="blue">{text}</Tag> : '-',
    },
    {
      title: 'Techada',
      dataIndex: 'ccl_techada',
      key: 'ccl_techada',
      width: 100,
      render: (techada) => (
        <Tag color={techada ? 'green' : 'default'}>
          {techada ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'ccl_activa',
      key: 'ccl_activa',
      width: 120,
      render: (activa) => (
        <Tag color={activa ? 'green' : 'red'}>
          {activa ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Está seguro de eliminar esta cancha?"
            onConfirm={() => handleDelete(record.ccl_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Administración de Canchas
          </h1>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCanchas}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nueva Cancha
            </Button>
          </Space>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {canchas.map(cancha => (
              <Card
                key={cancha.ccl_id}
                hoverable
                style={{
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {cancha.ccl_nombre}
                      </div>
                      {cancha.tipo_deporte_nombre && (
                        <div style={{ marginBottom: '4px' }}>
                          <Tag color="blue">{cancha.tipo_deporte_nombre}</Tag>
                        </div>
                      )}
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        {cancha.ccl_techada ? 'Techada' : 'Al aire libre'}
                      </div>
                    </div>
                    <Tag color={cancha.ccl_activa ? 'green' : 'red'}>
                      {cancha.ccl_activa ? 'Activa' : 'Inactiva'}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(cancha)}
                    >
                      Editar
                    </Button>
                    <Popconfirm
                      title="¿Está seguro de eliminar esta cancha?"
                      onConfirm={() => handleDelete(cancha.ccl_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={canchas}
            loading={loading}
            rowKey="ccl_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} canchas`,
            }}
          />
        )}

        {/* Modal para crear/editar */}
        <Modal
          title={editingCancha ? 'Editar Cancha' : 'Nueva Cancha'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ activa: true, techada: false }}
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ej: Cancha 1, Cancha 2, Cancha Central" />
            </Form.Item>

            <Form.Item
              name="tipoDeporte"
              label="Tipo de Deporte"
              rules={[{ required: true, message: 'El tipo de deporte es requerido' }]}
            >
              <Select placeholder="Seleccione un tipo de deporte">
                {tiposDeporte.map((tipo) => (
                  <Select.Option key={tipo.tdp_id} value={tipo.tdp_id}>
                    {tipo.tdp_nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="techada"
              label="Techada"
              valuePropName="checked"
            >
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>

            <Form.Item
              name="activa"
              label="Estado"
              valuePropName="checked"
            >
              <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionCanchas;
